// @story: none
import { Component, ChangeDetectionStrategy, inject, computed } from '@angular/core';
import { RouterOutlet, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { AuthService, PermissionService } from '@zag/auth/ui';
import { ACTIVE_LICENCES_DIALOG_CONFIG, ActiveLicencesDialogComponent, FeatureAccessClientService } from '@zag/features/ui';
import {
  SidebarNavComponent,
  SidebarNavStateService,
  SidebarProfileComponent,
  TopBarComponent,
  ToastContainerComponent,
  PageLayout,
  PageActionsOutletDirective,
  PageActionsService,
  LanguageService,
  ThemeService,
  BreadcrumbService,
  ZagIcon,
  Avatar,
  type ProfileUser,
  type NavConfigEntry,
  type BreadcrumbItem,
  type BreadcrumbDropdownOption,
  isNavSection,
} from '@zag/ui';
import { ActiveEnvironmentService } from '../services/active-environment.service';
import { CtrlMobileMenuDialogComponent } from './mobile-menu-dialog/mobile-menu-dialog.component';
import { CtrlProfileDialogComponent } from './profile-dialog/profile-dialog.component';
import {
  ENV_NAV_CONFIG,
  ADMIN_NAV_CONFIG,
  type PermissionAwareNavConfigEntry,
  type PermissionAwareNavItem,
  toNavConfigEntries,
} from '../navigation.config';

/**
 * Main layout for the control UI.
 *
 * Uses the same sidebar + content pattern as the main app for visual
 * consistency. The top bar breadcrumbs contain an environment switcher
 * dropdown followed by route-based breadcrumbs (e.g. tenant name with
 * sibling navigation). The sidebar shows:
 * - Environment-scoped nav items (dashboard, tenants, settings)
 * - Admin section (users, roles, groups, permissions)
 * - Profile section at the bottom
 */
@Component({
  selector: 'ctrl-layout',
  imports: [
    RouterOutlet,
    DialogModule,
    TranslatePipe,
    SidebarNavComponent,
    SidebarProfileComponent,
    TopBarComponent,
    ToastContainerComponent,
    PageLayout,
    PageActionsOutletDirective,
    ZagIcon,
    Avatar,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isSigningOut()) {
      <div class="ctrl-signing-out" aria-busy="true" aria-live="polite"></div>
    } @else {
      <div class="ctrl-layout">
        @if (currentUser()) {
          <aside class="ctrl-sidebar-column">
            <zag-sidebar-nav class="ctrl-sidebar" [entries]="navEntries()" [showSettings]="false">
              <zag-sidebar-profile
                slot="profile"
                [user]="profileUser()"
                [collapsed]="navState.isCollapsed()"
                [currentLanguage]="currentLanguage()"
                [currentTheme]="currentTheme()"
                [showNotifications]="false"
                (signOut)="onSignOut()"
                (versionInfoClick)="openActiveLicencesDialog()"
              />
            </zag-sidebar-nav>
          </aside>
        }

        <div class="ctrl-content-area">
          <header class="ctrl-topbar">
            <zag-top-bar [breadcrumbs]="breadcrumbs()" [showAIAction]="false" />
          </header>

          <main class="ctrl-content">
            <zag-page-layout [padding]="'lg'" class="ctrl-page-layout">
              <router-outlet />
            </zag-page-layout>
          </main>

          @if (hasPageActions()) {
            <div class="ctrl-page-footer">
              <div class="ctrl-page-footer__actions" zagPageActionsOutlet></div>
            </div>
          }
        </div>

        <!-- Mobile Bottom Navigation -->
        @if (currentUser()) {
          <nav class="ctrl-mobile-bottom-nav" [attr.aria-label]="'navigation.mainNavigation' | translate">
            <!-- Menu button -->
            <button
              type="button"
              class="ctrl-mobile-bottom-nav__action"
              (click)="openMobileMenuDialog()"
              [attr.aria-label]="'navigation.openMenu' | translate"
              [attr.title]="'navigation.openMenu' | translate"
            >
              <span zagIcon="menu"></span>
            </button>

            <!-- Profile button -->
            <button
              type="button"
              class="ctrl-mobile-bottom-nav__action"
              (click)="openProfileDialog()"
              [attr.aria-label]="'navigation.userMenu' | translate"
              [attr.title]="'navigation.userMenu' | translate"
            >
              <zag-avatar [name]="profileUser().name" [src]="profileUser().avatarUrl" size="md" />
            </button>
          </nav>
        }

        <zag-toast-container position="top-right" />
      </div>
    }
  `,
  styles: [
    `
      .ctrl-signing-out {
        position: fixed;
        inset: 0;
        background: var(--background-color-secondary);
      }

      .ctrl-layout {
        display: flex;
        flex-direction: row;
        height: 100vh;
        height: 100svh;
        overflow: hidden;
        background: var(--background-color-secondary);
        box-sizing: border-box;
      }

      .ctrl-sidebar-column {
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        height: 100%;
        overflow: hidden;
      }

      .ctrl-sidebar {
        flex: 1;
        min-height: 0;
      }

      .ctrl-content-area {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-width: 0;
        padding: var(--spacing-md);
        gap: var(--spacing-md);
      }

      .ctrl-topbar {
        display: flex;
        align-items: center;
        flex-shrink: 0;
      }

      .ctrl-topbar zag-top-bar {
        flex: 1;
      }

      .ctrl-content {
        flex: 1;
        min-height: 0;
        overflow: hidden;
        border-radius: var(--border-radius-md);
      }

      .ctrl-page-footer {
        position: relative;
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        flex-shrink: 0;
        min-height: 36px;
        justify-content: flex-end;
      }

      .ctrl-page-footer::after {
        content: ' ';
        position: absolute;
        top: calc(var(--spacing-md) * -1);
        left: 0;
        right: 0;
        height: 1px;
        background: var(--border-color-default);
        opacity: 0.5;
      }

      .ctrl-page-footer__actions {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        flex-wrap: nowrap;
        justify-content: flex-start;
      }

      @media print {
        .ctrl-layout {
          display: block;
          height: auto;
          overflow: visible;
          background: var(--surface-color-default);
        }

        .ctrl-sidebar-column,
        .ctrl-topbar,
        .ctrl-page-footer,
        .ctrl-mobile-bottom-nav,
        zag-toast-container {
          display: none !important;
        }

        .ctrl-content-area,
        .ctrl-content {
          display: block;
          height: auto;
          min-height: 0;
          overflow: visible;
          padding: 0;
        }

        .ctrl-page-layout {
          --zag-page-layout-height: auto;
          --zag-page-layout-overflow: visible;
          --zag-page-layout-container-height: auto;
          --zag-page-layout-container-overflow: visible;
          --zag-page-layout-container-padding: 0;
        }
      }

      .ctrl-mobile-bottom-nav {
        display: none;
      }

      @media (max-width: 539px) {
        .ctrl-layout {
          flex-direction: column;
        }

        .ctrl-sidebar-column {
          display: none;
        }

        .ctrl-content-area {
          padding: var(--spacing-sm);
          gap: var(--spacing-sm);
          padding-bottom: calc(60px + env(safe-area-inset-bottom));
          overflow: hidden;
        }

        .ctrl-page-footer::after {
          top: calc(var(--spacing-sm) * -1);
        }

        .ctrl-mobile-bottom-nav {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: calc(var(--z-index-modal) - 1);
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          align-items: center;
          gap: 0;
          min-height: 52px;
          padding: 0;
          padding-bottom: env(safe-area-inset-bottom);
          background: var(--background-color-elevated);
          border-top: 1px solid var(--border-color-default);
          box-shadow: var(--shadow-md);
        }

        .ctrl-mobile-bottom-nav > * {
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          height: 52px;
          overflow: hidden;
        }

        .ctrl-mobile-bottom-nav__action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 52px;
          border: none;
          border-radius: 0;
          background: transparent;
          color: var(--text-color-secondary);
          cursor: pointer;
          margin: 0;
          padding: 0;

          &:hover {
            background: var(--surface-color-hover);
            color: var(--text-color-primary);
          }

          span[zagIcon] {
            font-size: 24px;
          }
        }
      }
    `,
  ],
})
export class ControlLayoutComponent {
  private readonly authService = inject(AuthService);
  private readonly activeEnvService = inject(ActiveEnvironmentService);
  private readonly permissionService = inject(PermissionService);
  private readonly languageService = inject(LanguageService);
  private readonly themeService = inject(ThemeService);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(Dialog);
  private readonly featureAccess = inject(FeatureAccessClientService);
  private readonly router = inject(Router);
  private readonly pageActionsService = inject(PageActionsService);
  protected readonly navState = inject(SidebarNavStateService);
  protected readonly hasPageActions = this.pageActionsService.hasPortal;

  // User state
  protected readonly currentUser = computed(() => this.authService.currentUser());

  // Hides the protected layout during sign-out so the user does not see
  // half-rendered chrome while the route swaps to /login.
  protected readonly isSigningOut = this.authService.isSigningOut;
  protected readonly profileUser = computed((): ProfileUser => {
    const user = this.currentUser();
    return {
      name: user?.name || user?.email || 'User',
      email: user?.email,
      avatarUrl: user?.image ?? undefined,
    };
  });

  // Language/Theme
  protected readonly currentLanguage = this.languageService.currentLanguage;
  protected readonly currentTheme = this.themeService.currentTheme;

  // Environment state
  protected readonly environments = this.activeEnvService.environments;
  protected readonly activeSlug = this.activeEnvService.activeSlug;
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly canAccessEnvironments = computed(() =>
    this.permissionService.hasAnyPermission(['environments:read', 'environments:create'])
  );

  /**
   * Breadcrumb items for the top bar.
   *
   * Combines the environment switcher dropdown (first item) with
   * route-based breadcrumbs generated by `BreadcrumbService` (e.g.
   * "Tenants > alpenblick-services [siblings]" on the tenant detail page).
   */
  protected readonly breadcrumbs = computed((): BreadcrumbItem[] => {
    if (!this.canAccessEnvironments()) {
      return [];
    }

    const envs = this.environments();
    const options: BreadcrumbDropdownOption[] = envs.map(env => ({
      label: env.name,
      value: env.slug,
      icon: env.provider === 'local' ? 'computer' : 'dns',
    }));

    const envDropdown: BreadcrumbItem = {
      type: 'dropdown',
      icon: 'dns',
      options,
      value: this.activeSlug(),
      placeholder: this.translateService.instant('control.nav.switchEnvironment'),
      onSelect: (value: string | null) => this.onBreadcrumbEnvSelect(value),
    };

    // Merge with route-based breadcrumbs from BreadcrumbService
    const routeBreadcrumbs = this.breadcrumbService.breadcrumbs();

    return [envDropdown, ...routeBreadcrumbs];
  });

  /**
   * Build navigation entries by replacing `:slug` placeholder with the
   * active environment slug and combining env-scoped + admin entries.
   */
  protected readonly navEntries = computed((): NavConfigEntry[] => {
    const slug = this.activeSlug();
    const adminEntries = this.filterNavEntriesByPermission(ADMIN_NAV_CONFIG);
    if (!slug) return toNavConfigEntries(adminEntries);

    const envEntries = this.replaceSlugInEntries(this.filterNavEntriesByPermission(ENV_NAV_CONFIG), slug);
    return toNavConfigEntries([...envEntries, ...adminEntries]);
  });

  /**
   * Handle environment breadcrumb dropdown selection.
   * Switches to the selected environment.
   */
  private onBreadcrumbEnvSelect(value: string | null): void {
    if (value) {
      this.activeEnvService.setActive(value);
    }
  }

  protected async onSignOut(): Promise<void> {
    await this.authService.confirmSignOut();
  }

  protected openMobileMenuDialog(): void {
    this.dialog.open(CtrlMobileMenuDialogComponent, {
      width: 'min(96vw, 720px)',
      maxWidth: '96vw',
      maxHeight: '96svh',
      panelClass: 'ai-chat-dialog-panel',
      backdropClass: 'ai-chat-dialog-backdrop',
      data: {
        navEntries: this.navEntries(),
      },
    });
  }

  protected openProfileDialog(): void {
    const dialogRef = this.dialog.open(CtrlProfileDialogComponent, {
      width: 'min(96vw, 720px)',
      maxWidth: '96vw',
      maxHeight: '96svh',
      panelClass: 'ai-chat-dialog-panel',
      backdropClass: 'ai-chat-dialog-backdrop',
      data: {
        user: this.profileUser(),
      },
    });

    dialogRef.closed.subscribe(result => {
      if (!result || typeof result !== 'object' || !('action' in result)) return;

      const typedResult = result as {
        action: string;
      };

      switch (typedResult.action) {
        case 'signOut':
          this.onSignOut();
          break;
        case 'navigateToProfile':
          this.router.navigate(['/admin/users/' + this.currentUser()?.email]);
          break;
      }
    });
  }

  protected openActiveLicencesDialog(): void {
    this.dialog.open(ActiveLicencesDialogComponent, {
      ...ACTIVE_LICENCES_DIALOG_CONFIG,
      data: { licences: this.featureAccess.licences() ?? [] },
    });
  }

  private filterNavEntriesByPermission(entries: PermissionAwareNavConfigEntry[]): PermissionAwareNavConfigEntry[] {
    return entries
      .map(entry => {
        if (isNavSection(entry)) {
          const items = entry.items.filter(item => this.isNavItemVisible(item));
          return {
            ...entry,
            items,
          };
        }

        return entry;
      })
      .filter(entry => {
        if (isNavSection(entry)) {
          return entry.items.length > 0;
        }

        return this.isNavItemVisible(entry);
      });
  }

  private isNavItemVisible(item: PermissionAwareNavItem): boolean {
    if (!item.requiredAnyPermissions || item.requiredAnyPermissions.length === 0) {
      return true;
    }

    return this.permissionService.hasAnyPermission(item.requiredAnyPermissions);
  }

  private replaceSlugInEntries(entries: PermissionAwareNavConfigEntry[], slug: string): PermissionAwareNavConfigEntry[] {
    return entries.map(entry => {
      if (isNavSection(entry)) {
        return {
          ...entry,
          items: entry.items.map(item => this.replaceSlugInItem(item, slug)),
        };
      }
      return this.replaceSlugInItem(entry, slug);
    });
  }

  private replaceSlugInItem(item: PermissionAwareNavItem, slug: string): PermissionAwareNavItem {
    return {
      ...item,
      route: item.route?.replace(':slug', slug),
    };
  }
}
